import type {
  AnalysisSpec,
  AnalyticsBootstrap,
  AnalyticsContentScope,
  AnalyticsUserPreference,
  AskRequest,
  ChartTemplate,
  DashboardDatePreset,
  DashboardDefinition,
  EvidenceFact,
  SemanticFilter,
  SemanticQueryTrace,
  VisualAnswer,
  VisualDefinition,
  VisualSpec,
  VisualExecutionMeta,
  AnalyticsInsightJob,
  AnalyticsInsightRevision,
  AnalyticsInsightThread,
  AnalyticsDashboardSnapshot,
  AnalyticsDashboardSnapshotItem,
  AnalyticsCompanySettings,
  ExecutionContext,
  ChartThemeDefinition,
  ChartThemePayload,
  ChartThemeSummary,
  PortableChartTheme,
  ThemeAccessibilityCheck,
  ThemeGenerationResult,
} from '@lognai/plugin-analytics';
import { apiFetch, apiPost } from '../api/client';
import { getApiHeaders, getCompanyId } from '@lognai/plugin-sdk';

export const getMetricDefinitions = (view: string, members: string[]): Promise<import('@lognai/plugin-analytics').MetricSourceDefinition> =>
  apiPost('/api/analytics/semantic/definitions', { view, members });

export interface AnalysisResult {
  rows?: Array<Record<string, string | number | null>>;
  context?: ExecutionContext;
  analysis: AnalysisSpec;
  visual: VisualSpec;
  evidence: EvidenceFact[];
  trace: SemanticQueryTrace[];
  meta?: VisualExecutionMeta;
}

export interface VisualPage {
  items: VisualDefinition[];
  total: number;
  page: number;
  pageSize: number;
  facets: { domains: Record<string, number>; templates: Record<string, number>; scopes: Record<string, number>;
  };
  usage: Record<string, { score: number; lastUsedAt?: string }>;
}

export interface BatchResult {
  visualId: string;
  ok: boolean;
  result?: AnalysisResult;
  error?: string;
}

/**
 * Bootstrap, shared for a few seconds across whoever asks.
 *
 * /analytics fetches it only to learn the default dashboard id, then redirects
 * to the dashboard page, which needs the same payload — two identical requests
 * on the module's most common entry path, on the critical path of a load the
 * user is already waiting through (QA ledger ANLY-005). The window is short
 * enough that nothing stale survives a navigation, and it is dropped on failure
 * so a retry is a real retry, and on a preference write so a newly-set default
 * dashboard is picked up immediately.
 */
const BOOTSTRAP_TTL_MS = 5_000;
let bootstrapCache: { key: string; at: number; promise: Promise<AnalyticsBootstrap> } | undefined;

export const invalidateAnalyticsBootstrap = (): void => { bootstrapCache = undefined; };

export const getAnalyticsBootstrap = (force = false): Promise<AnalyticsBootstrap> => {
  if (force) invalidateAnalyticsBootstrap();
  const key = JSON.stringify([getCompanyId(), getApiHeaders()]);
  if (bootstrapCache &&
    bootstrapCache.key === key && Date.now() - bootstrapCache.at < BOOTSTRAP_TTL_MS) return bootstrapCache.promise;
  const promise = apiFetch<AnalyticsBootstrap>('/api/analytics/bootstrap');
  bootstrapCache = { key, at: Date.now(), promise };
  promise.catch(() => invalidateAnalyticsBootstrap());
  return promise;
};

export const THEME_CHANGED = 'analytics:themes:changed';
const announceThemeChange = <T>(result: T): T => {
  invalidateAnalyticsBootstrap();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(THEME_CHANGED));
  return result;
};

export const listChartThemes = (includeArchived = false): Promise<{ items: ChartThemeSummary[] }> =>
  apiFetch(`/api/analytics/themes${includeArchived ? '?includeArchived=true' : ''}`);

export const getChartTheme = (id: string): Promise<{ theme: ChartThemeDefinition; portable: PortableChartTheme; checks: ThemeAccessibilityCheck[];
}> =>
  apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}`);

export const createChartTheme = (input:
    | { name: string; description: string; draft: ChartThemePayload } | { portable: PortableChartTheme }): Promise<ChartThemeDefinition> =>
  apiPost<ChartThemeDefinition>('/api/analytics/themes', input).then(announceThemeChange);

export const updateChartTheme = (id: string, expectedVersion: number, patch: { name?: string; description?: string; draft?: ChartThemePayload }): Promise<ChartThemeDefinition> =>
  apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion, patch }),
  }).then((result) => announceThemeChange(result as ChartThemeDefinition));

export const cloneChartTheme = (id: string): Promise<ChartThemeDefinition> =>
  apiPost<ChartThemeDefinition>(`/api/analytics/themes/${encodeURIComponent(id)}/clone`, {}).then(announceThemeChange);

export const publishChartTheme = (id: string, expectedVersion: number, confirmWarnings = false): Promise<ChartThemeDefinition> =>
  apiPost<ChartThemeDefinition>(`/api/analytics/themes/${encodeURIComponent(id)}/publish`, { expectedVersion, confirmWarnings }).then(announceThemeChange);

export const setDefaultChartTheme = (id: string, expectedVersion: number): Promise<ChartThemeDefinition> =>
  apiPost<ChartThemeDefinition>(`/api/analytics/themes/${encodeURIComponent(id)}/set-default`, { expectedVersion }).then(announceThemeChange);

export const archiveChartTheme = (id: string, expectedVersion: number): Promise<{ archived: true }> =>
  apiFetch<{ archived: true }>(`/api/analytics/themes/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' }).then(announceThemeChange);

export const validateChartTheme = (draft: ChartThemePayload): Promise<{ valid: true; checks: ThemeAccessibilityCheck[] }> =>
  apiPost('/api/analytics/themes/validate', { draft });

export const generateChartTheme = (prompt: string, currentDraft?: ChartThemePayload, fromLogo?: boolean): Promise<ThemeGenerationResult> =>
  apiPost('/api/analytics/themes/generate', { prompt, currentDraft, ...(fromLogo ? { fromLogo } : {}) });

export const getThemePublishImpact = (id: string): Promise<import('@lognai/plugin-analytics').ThemePublishImpact> => apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}/impact`);
export const getThemeVersion = (id: string, version: number): Promise<import('@lognai/plugin-analytics').PublishedChartTheme> => apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}/versions/${version}`);

export interface ResolvedChartThemeResponse {
  version?: number;
  id: string;
  name: string;
  source: 'dashboard' | 'company' | 'system';
  payload: ChartThemePayload;
  requestedThemeUnavailable: boolean;
}

export const resolveChartTheme = (dashboardId?: string, themeId?: string): Promise<ResolvedChartThemeResponse> => {
  const query = new URLSearchParams();
  if (dashboardId) query.set('dashboardId', dashboardId);
  if (themeId) query.set('themeId', themeId);
  return apiFetch(`/api/analytics/themes/resolve${query.size ? `?${query}` : ''}`);
};

export const listVisuals = (params: {
  q?: string; domains?: string[]; templates?: ChartTemplate[]; scopes?: AnalyticsContentScope[];
  favorites?: boolean; sort?: 'relevance' | 'popular' | 'recent' | 'title'; page?: number; pageSize?: number;
} = {}): Promise<VisualPage> => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.domains?.length) query.set('domains', params.domains.join(','));
  if (params.templates?.length) query.set('templates', params.templates.join(','));
  if (params.scopes?.length) query.set('scopes', params.scopes.join(','));
  if (params.favorites) query.set('favorites', 'true');
  if (params.sort) query.set('sort', params.sort);
  query.set('page', String(params.page ?? 1));
  query.set('pageSize', String(params.pageSize ?? 24));
  return apiFetch(`/api/analytics/visuals?${query}`);
};

export const getVisual = (id: string): Promise<VisualDefinition> => apiFetch(`/api/analytics/visuals/${encodeURIComponent(id)}`);
export const cloneVisual = (id: string): Promise<VisualDefinition> => apiPost(`/api/analytics/visuals/${encodeURIComponent(id)}/clone`, {});
export const renameVisual = (id: string, expectedVersion: number, title: string): Promise<VisualDefinition> =>
  apiFetch(`/api/analytics/visuals/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expectedVersion, patch: { title: title.trim() } }),
  });
export const deleteVisual = (id: string, expectedVersion: number): Promise<VisualDefinition> =>
  apiFetch(`/api/analytics/visuals/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });

export const executeAnalysis = (analysis: AnalysisSpec): Promise<AnalysisResult> =>
  apiPost('/api/analytics/visuals/preview', { analysis });

export const executeVisualBatch = (items: Array<{ visualId: string; filters?: SemanticFilter[]; dateRange?: [string, string] }>): Promise<{ results: BatchResult[] }> =>
  apiPost('/api/analytics/visuals/execute-batch', { items });

export const askAnalytics = (request: AskRequest): Promise<VisualAnswer> => apiPost('/api/analytics/ask', request);

export const createVisual = (input: {
  executionId?: string;
  title: string; description: string; businessQuestion: string; scope: 'private' | 'company'; origin: 'manual' | 'ai';
  analysis: AnalysisSpec; presentation: { template: ChartTemplate }; domains: string[]; workflows: string[]; tags: string[];
}): Promise<VisualDefinition> => apiPost('/api/analytics/visuals', input);

export const listDashboards = (options: { includeArchived?: boolean; deduplicate?: boolean } = {}): Promise<DashboardDefinition[]> => {
  const query = new URLSearchParams();
  if (options.includeArchived) query.set('includeArchived', 'true');
  if (options.deduplicate === false) query.set('deduplicate', 'false');
  return apiFetch(`/api/analytics/dashboards${query.size ? `?${query}` : ''}`);
};
export const getDashboard = (id: string): Promise<DashboardDefinition> => apiFetch(`/api/analytics/dashboards/${encodeURIComponent(id)}`);
export const createDashboard = (input: {
  key: string;
  name: string;
  description: string;
  scope: 'private' | 'company';
  tabs?: DashboardDefinition['tabs'];
  defaultTabId?: string;
  widgets: DashboardDefinition['widgets'];
  layouts: DashboardDefinition['layouts'];
  filters: DashboardDefinition['filters'];
  filterPanel?: DashboardDefinition['filterPanel'];
  domains: string[];
  workflows: string[];
  tags: string[];
  isCompanyDefault?: boolean;
  themeId?: string | null;
  insights?: DashboardDefinition['insights'];
  history?: DashboardDefinition['history'];
  kpis?: DashboardDefinition['kpis'];
  kpiLayouts?: DashboardDefinition['kpiLayouts'];
  presentation?: DashboardDefinition['presentation'];
}): Promise<DashboardDefinition> => apiPost('/api/analytics/dashboards', input);
export const cloneDashboard = (id: string): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/clone`, {});
export const makeDashboardEditable = (id: string): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/editable`, {});
export const resetDashboard = (id: string, expectedVersion: number): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/reset`, { expectedVersion });
export const revertDashboard = (id: string, expectedVersion: number): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/revert`, { expectedVersion });

export interface DashboardBriefingResult {
  dashboardId: string;
  datePreset: DashboardDatePreset;
  kpis: Array<{
    definition: NonNullable<DashboardDefinition['kpis']>[number];
    value: number;
    detailValues?: Array<{ value: number; unit: string; currency?: string }>;
    previousValue?: number;
    deltaPct?: number;
    unit: 'currency' | 'percent' | 'count' | 'days' | 'number';
    currency?: string;
    freshnessAt: string;
    trace: SemanticQueryTrace;
  }>;
  insight: VisualAnswer['insight'];
  evidence: EvidenceFact[];
  freshnessAt: string;
}

export const getDashboardBriefing = (id: string, datePreset: DashboardDatePreset, includeAi = false, filters?: SemanticFilter[], dateRange?: [string, string], tabId?: string): Promise<DashboardBriefingResult> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/briefing`, { datePreset, includeAi, filters, dateRange, tabId });

export const recordVisualUsage = (id: string, action: 'previewed' | 'added_to_dashboard' | 'created'): Promise<{ recorded: true }> =>
  apiPost(`/api/analytics/visuals/${encodeURIComponent(id)}/usage`, { action });

export interface DashboardOwner { id: string; name: string; email: string }
export const listDashboardOwners = (id: string): Promise<DashboardOwner[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(id)}/owners`);

export const updateDashboard = async (id: string, expectedVersion: number, patch: Omit<Partial<DashboardDefinition>, 'themeId'> & { themeId?: string | null }): Promise<DashboardDefinition> => {
  const updated = await apiFetch<DashboardDefinition>(`/api/analytics/dashboards/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion, patch }),
  });
  invalidateAnalyticsBootstrap();
  return updated;
};

export const archiveDashboard = async (id: string, expectedVersion: number): Promise<DashboardDefinition> => {
  const result = await apiFetch<DashboardDefinition>(`/api/analytics/dashboards/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });
  invalidateAnalyticsBootstrap();
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('lognai.analytics.defaultDashboardId') === id)
      sessionStorage.removeItem('lognai.analytics.defaultDashboardId');
  } catch { /* Session storage can be unavailable. */ }
  return result;
};

export const updatePreferences = (patch: Partial<AnalyticsUserPreference>): Promise<AnalyticsUserPreference> => {
  invalidateAnalyticsBootstrap();
  return apiFetch('/api/analytics/preferences', {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });
};

export interface ActiveInsight {
  thread: AnalyticsInsightThread;
  revision: AnalyticsInsightRevision;
  stale: boolean;
}

export const listDashboardInsights = (dashboardId: string, datePreset?: DashboardDatePreset, tabId?: string): Promise<ActiveInsight[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights?${new URLSearchParams({ ...(datePreset ? { datePreset } : {}), ...(tabId ? { tabId } : {}) })}`);

export const saveManualInsight = (dashboardId: string, input: {
  targetType: 'dashboard' | 'widget'; tabId?: string; widgetId?: string; headline: string; summary: string; expectedThreadVersion?: number; editThreadId?: string;
}): Promise<ActiveInsight> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/manual`, input);

export const createPersistedInsightJob = (dashboardId: string, input: {
  targetType: 'dashboard' | 'widget'; tabId?: string; widgetId?: string; question: string; analysis: AnalysisSpec;
  selection?: { member: string; key: string; label?: string };
  context: { datePreset: DashboardDatePreset; filters: SemanticFilter[]; drillState: Array<{ member: string; key: string }>; visualVersion?: number;
    };
}): Promise<AnalyticsInsightJob> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insight-jobs`, input);

export const getPersistedInsightJob = (dashboardId: string, jobId: string): Promise<AnalyticsInsightJob> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insight-jobs/${encodeURIComponent(jobId)}`);

export const getInsightRevisions = (dashboardId: string, threadId: string): Promise<AnalyticsInsightRevision[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/${encodeURIComponent(threadId)}/revisions`);

export const archiveInsight = (dashboardId: string, threadId: string, expectedVersion: number): Promise<AnalyticsInsightThread> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/${encodeURIComponent(threadId)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });

export const listDashboardSnapshots = (dashboardId: string): Promise<AnalyticsDashboardSnapshot[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots`);

export const createDashboardSnapshot = (dashboardId: string, input: { label?: string; datePreset?: DashboardDatePreset }): Promise<AnalyticsDashboardSnapshot> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots`, input);

export const getDashboardSnapshot = (dashboardId: string, snapshotId: string): Promise<{ snapshot: AnalyticsDashboardSnapshot; items: AnalyticsDashboardSnapshotItem[] }> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}`);

export interface DashboardComparisonResult {
  dashboardId: string;
  snapshot: AnalyticsDashboardSnapshot;
  historicalItems: AnalyticsDashboardSnapshotItem[];
  current: { capturedAt: string; datePreset: DashboardDatePreset };
  kpis: Array<{
    id: string; label: string; unit: 'currency' | 'percent' | 'count' | 'days' | 'number';
    current?: number; historical?: number; classification: string;
    delta?: { absolute: number; percentage?: number };
  }>;
  widgets: Array<{
    widgetId: string; visualId: string; classification: string;
    current?: { ok: boolean; result?: { execution: { visual: VisualSpec; analysis: AnalysisSpec; evidence: EvidenceFact[]; trace: SemanticQueryTrace;
        }; meta: VisualExecutionMeta;
      };
    };
    historical?: AnalyticsDashboardSnapshotItem;
  }>;
}

export const compareDashboardSnapshot = (dashboardId: string, snapshotId: string, tabId?: string): Promise<DashboardComparisonResult> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}/compare${tabId ? `?tabId=${encodeURIComponent(tabId)}` : ''}`, {});

export const createComparisonInsightJob = (dashboardId: string, snapshotId: string, question = 'What changed since this dashboard snapshot?', tabId?: string): Promise<AnalyticsInsightJob> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}/insight-jobs`, { question, tabId });

export const getAnalyticsHistorySettings = (): Promise<AnalyticsCompanySettings> => apiFetch('/api/analytics/history-settings');

export const updateAnalyticsHistorySettings = async (input: {
  expectedVersion: number; historyRetentionDays?: number; captureLocalHour?: number; timezone?: string;
}): Promise<AnalyticsCompanySettings> => {
  const settings = await apiFetch<AnalyticsCompanySettings>('/api/analytics/history-settings', {
  method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
  invalidateAnalyticsBootstrap();
  return settings;
};

export interface FilterValuesResult { member: string; ok: boolean; values: Array<{ value: string; count?: number }>; hasMore: boolean; nextOffset?: number; error?: string }
export const getFilterValues = (items: Array<{ member: string; search?: string; offset?: number; limit?: number; order?: 'count' | 'label'; filters?: SemanticFilter[] }>): Promise<{ results: FilterValuesResult[] }> => apiPost('/api/analytics/filters/values', { items });
